import { Routes, Route, Navigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";

import OrgOverview from "@/pages/org/Overview";
import OrgUsers from "@/pages/org/OrgUsers";
import OrgAuditCA from "@/pages/org/OrgAuditCA";
import OrgAuditDetail from "@/pages/org/OrgAuditDetail";
import OrgVendors from "@/pages/org/OrgVendors";
import OrgCustomers from "@/pages/org/OrgCustomers";
import OrgProfile from "@/pages/org/OrgProfile";
import OrgDispatch from "@/pages/org/OrgDispatch";
import OrgSubscription from "@/pages/org/OrgSubscription";

// Procurement & Purchase
import PurchaseHub from "@/pages/org/hubs/PurchaseHub";
import Requisitions from "@/pages/procurement/Requisitions";
import PurchaseOrders from "@/pages/procurement/PurchaseOrders";
import GRN from "@/pages/procurement/GRN";
import Approvals from "@/pages/procurement/Approvals";
import ApprovalRules from "@/pages/procurement/ApprovalRules";

// Inventory
import Items from "@/pages/inventory/Items";
import StockHub from "@/pages/org/hubs/StockHub";
import RawStock from "@/pages/inventory/RawStock";
import FinishedStock from "@/pages/inventory/FinishedStock";
import RawDaily from "@/pages/inventory/RawDaily";
import FinishedDaily from "@/pages/inventory/FinishedDaily";
import FinishedProducts from "@/pages/inventory/FinishedProducts";
import Stock from "@/pages/inventory/Stock"; // legacy combined view (for /movements)

// QC
import QCHub from "@/pages/org/hubs/QCHub";
import QCBucket from "@/pages/qc/QCBucket";

// Manufacturing / Production
import ProductionHub from "@/pages/org/hubs/ProductionHub";
import Production from "@/pages/manufacturing/Production";
import ProductionRuns from "@/pages/manufacturing/ProductionRuns";
import SalesOrdersQueue from "@/pages/manufacturing/SalesOrdersQueue";
import KilnLines from "@/pages/manufacturing/KilnLines";

// Finance
import FinanceHub from "@/pages/org/hubs/FinanceHub";
import Finance from "@/pages/finance/Finance";
import FinanceReceivables from "@/pages/finance/FinanceReceivables";
import FinancePayables from "@/pages/finance/FinancePayables";

// Settings & governance
import AuditLog from "@/pages/settings/AuditLog";
import MfaSettings from "@/pages/settings/Mfa";
import Roles from "@/pages/org/Roles";
import Security from "@/pages/org/Security";
import BulkUploadPermissions from "@/pages/org/BulkUploadPermissions";
import Sessions from "@/pages/org/Sessions";
import LoginAudit from "@/pages/org/LoginAudit";

export default function OrgDashboard() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  // CA role is scoped strictly to CA Audit page
  if (user.role === "CA" && !window.location.pathname.startsWith("/org/audit-ca")) {
    return <Navigate to="/org/audit-ca" replace />;
  }
  return (
    <AppShell variant="org">
      <Routes>
        <Route path="/" element={<OrgOverview />} />

        {/* Purchase hub + leaves */}
        <Route path="/purchase" element={<PurchaseHub />} />
        <Route path="/requisitions" element={<Requisitions />} />
        <Route path="/purchase-orders" element={<PurchaseOrders />} />
        <Route path="/grn" element={<GRN />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/approval-rules" element={<ApprovalRules />} />

        {/* Items */}
        <Route path="/items" element={<Items />} />

        {/* Stock hub + split ledgers */}
        <Route path="/stock" element={<StockHub />} />
        <Route path="/stock/raw" element={<RawStock />} />
        <Route path="/stock/finished" element={<FinishedStock />} />
        <Route path="/stock/raw-daily" element={<RawDaily />} />
        <Route path="/stock/finished-daily" element={<FinishedDaily />} />
        <Route path="/stock/finished-products" element={<FinishedProducts />} />
        <Route path="/stock/movements" element={<Stock />} />
        <Route path="/stock/warehouses" element={<Stock />} />

        {/* QC hub + buckets */}
        <Route path="/qc" element={<QCHub />} />
        <Route path="/qc/incoming" element={<QCBucket bucket="incoming" />} />
        <Route path="/qc/manufacturing" element={<QCBucket bucket="manufacturing" />} />
        <Route path="/qc/returns" element={<QCBucket bucket="returns" />} />

        {/* Production hub */}
        <Route path="/production" element={<ProductionHub />} />
        <Route path="/production/orders" element={<SalesOrdersQueue />} />
        <Route path="/production/runs" element={<ProductionRuns />} />
        <Route path="/production/lines" element={<KilnLines />} />

        {/* Finance hub + receivables/payables */}
        <Route path="/finance" element={<FinanceHub />} />
        <Route path="/finance/receivables" element={<FinanceReceivables />} />
        <Route path="/finance/payables" element={<FinancePayables />} />
        {/* Legacy deep-links now redirect into Receivables (ledger lives as a tab there) */}
        <Route path="/finance/ledger" element={<Navigate to="/org/finance/receivables" replace />} />
        <Route path="/finance/aging" element={<Navigate to="/org/finance/receivables" replace />} />
        <Route path="/finance/notes" element={<Navigate to="/org/finance/receivables" replace />} />
        <Route path="/finance/payments" element={<Navigate to="/org/finance/receivables" replace />} />

        {/* Governance & Settings */}
        <Route path="/integrations" element={<Navigate to="/org" replace />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="/audit-ca" element={<OrgAuditCA />} />
        <Route path="/audit-ca/:kind" element={<OrgAuditDetail />} />
        <Route path="/users" element={<OrgUsers />} />
        <Route path="/vendors" element={<OrgVendors />} />
        <Route path="/customers" element={<OrgCustomers />} />
        <Route path="/profile" element={<OrgProfile />} />
        <Route path="/dispatch" element={<OrgDispatch />} />
        <Route path="/subscription" element={<OrgSubscription />} />
        <Route path="/settings/mfa" element={<MfaSettings />} />
        <Route path="/roles" element={<Roles />} />
        <Route path="/bulk-upload-permissions" element={<BulkUploadPermissions />} />
        <Route path="/security" element={<Security />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/login-audit" element={<LoginAudit />} />
      </Routes>
    </AppShell>
  );
}
